import { apiConfig, makeApiRequest } from './apiConfig';

export interface ContractGroup {
  id: string;
  _id?: string;
  name: string;
  description?: string;
  color: string;
  icon: string;
  isActive: boolean;
  sortOrder: number;
  createdBy?: any;
  updatedBy?: any;
  createdAt: string;
  updatedAt: string;
}

export interface CreateContractGroupData {
  name: string;
  description?: string;
  color?: string;
  icon?: string;
  sortOrder?: number;
}

export interface UpdateContractGroupData {
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  isActive?: boolean;
  sortOrder?: number;
}

export const contractGroupService = {
  /**
   * Get all contract groups
   */
  async getAll(token: string): Promise<ContractGroup[]> {
    try {
      const response = await makeApiRequest<ContractGroup[]>(
        apiConfig.endpoints.contractGroups.getAll,
        { method: 'GET' },
        token
      );
      return response.data || [];
    } catch (error) {
      console.error('Error fetching contract groups:', error);
      return [];
    }
  },

  /**
   * Get active contract groups only
   */
  async getActive(token: string): Promise<ContractGroup[]> {
    try {
      const response = await makeApiRequest<ContractGroup[]>(
        apiConfig.endpoints.contractGroups.getActive,
        { method: 'GET' },
        token
      );
      return response.data || [];
    } catch (error) {
      console.error('Error fetching active contract groups:', error);
      return [];
    }
  },

  /**
   * Get a single contract group by ID
   */
  async getOne(token: string, id: string): Promise<ContractGroup | null> {
    try {
      const response = await makeApiRequest<ContractGroup>(
        apiConfig.endpoints.contractGroups.getOne(id),
        { method: 'GET' },
        token
      );
      return response.data || null;
    } catch (error) {
      console.error('Error fetching contract group:', error);
      return null;
    }
  },

  /**
   * Create a new contract group
   */
  async create(token: string, data: CreateContractGroupData): Promise<ContractGroup | null> {
    try {
      const response = await makeApiRequest<ContractGroup>(
        apiConfig.endpoints.contractGroups.create,
        {
          method: 'POST',
          body: JSON.stringify(data)
        },
        token
      );
      return response.data || null;
    } catch (error) {
      console.error('Error creating contract group:', error);
      throw error;
    }
  },

  /**
   * Update a contract group
   */
  async update(token: string, id: string, data: UpdateContractGroupData): Promise<ContractGroup | null> {
    try {
      const response = await makeApiRequest<ContractGroup>(
        apiConfig.endpoints.contractGroups.update(id),
        {
          method: 'PUT',
          body: JSON.stringify(data)
        },
        token
      );
      return response.data || null;
    } catch (error) {
      console.error('Error updating contract group:', error);
      throw error;
    }
  },

  /**
   * Delete a contract group
   */
  async delete(token: string, id: string): Promise<boolean> {
    try {
      await makeApiRequest(
        apiConfig.endpoints.contractGroups.delete(id),
        { method: 'DELETE' },
        token
      );
      return true;
    } catch (error) {
      console.error('Error deleting contract group:', error);
      throw error;
    }
  }
};

export default contractGroupService;
